/**
 * Vertical Editor Page
 *
 * Displays configuration files for a vertical with:
 * - File tree sidebar
 * - Monaco editor for selected file
 * - Validation panel
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Loader2, AlertCircle, RefreshCw, FileJson, Home } from "lucide-react";
import type { ReactElement } from "react";
import { useParams, Link } from "react-router-dom";

import { apiRequest } from "../api/client";
import { CONFIG_TYPE_LABELS, type ConfigType } from "../types";

interface ConfigFile {
  readonly type: ConfigType;
  readonly path: string;
  readonly status: "draft" | "live" | "archived";
  readonly version: number;
  readonly hasChanges: boolean;
  readonly lastModified?: string;
}

interface VerticalConfig {
  readonly key: string;
  readonly name: string;
  readonly description?: string;
  readonly configs: readonly ConfigFile[];
}

export function VerticalEditorPage(): ReactElement {
  const { verticalKey } = useParams<{ verticalKey: string }>();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["vertical", verticalKey],
    queryFn: async () => {
      const response = await apiRequest<VerticalConfig>(`/api/v1/verticals/${verticalKey}/configs`);
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!verticalKey,
  });

  if (!verticalKey) {
    return (
      <div className="flex h-full items-center justify-center text-foreground-muted">
        No vertical selected
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 border-b border-border bg-background-secondary px-4 py-2 text-sm">
        <Link
          to="/studio"
          className="flex items-center gap-1 text-foreground-muted hover:text-foreground"
        >
          <Home className="h-4 w-4" />
          <span>Studio</span>
        </Link>
        <ChevronRight className="h-4 w-4 text-foreground-muted" />
        <span className="font-medium">{data?.name ?? verticalKey}</span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : "Failed to load vertical"}
            onRetry={refetch}
          />
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold">{data?.name ?? verticalKey}</h1>
              {data?.description && (
                <p className="mt-1 text-foreground-muted">{data.description}</p>
              )}
            </div>

            {/* Config files grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data?.configs.map((config) => (
                <ConfigCard key={config.type} config={config} verticalKey={verticalKey} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface ConfigCardProps {
  readonly config: ConfigFile;
  readonly verticalKey: string;
}

function ConfigCard({ config, verticalKey }: ConfigCardProps): ReactElement {
  const label = CONFIG_TYPE_LABELS[config.type];

  return (
    <Link
      to={`/studio/${verticalKey}/${config.type}`}
      className="group card flex items-start gap-3 hover:border-primary/50 transition-colors"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <FileJson className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{label}</h3>
          {config.hasChanges && (
            <span className="inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
              Modified
            </span>
          )}
        </div>
        <p className="text-sm text-foreground-muted">
          Version {config.version} - {config.status}
        </p>
        {config.lastModified && (
          <p className="mt-1 text-xs text-foreground-muted">
            {new Date(config.lastModified).toLocaleDateString()}
          </p>
        )}
      </div>
      <ChevronRight className="h-5 w-5 text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading configuration...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}
